import Async from "components/DesignSystem/AsyncComponents/Async";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { ListIcon } from "components/icons/ListIcon";
import { useChannelNewMessageListener } from "hooks/useChannelNewMessageListener";
import { useCallback } from "react";
import { FilingsSkeleton } from "v2/components/FilingsDashboard/DetectedFilingsCard";
import {
  FilingsGroupBySeason,
  NoFilingEmptyState,
} from "v2/components/entityDocuments/FilingsDetectedPanel";
import { DashboardEntity } from "v2/pages/FilingsDashboard/FilingsDashboard";
import {
  useGetDetectedFilingsCountQuery,
  useGetDetectedFilingsQuery,
} from "v2/store/taxSetupApi";
import { groupFilingsBySeason } from "v2/utils/mapDetectedFilings";

type DetectedFilingsProps = {
  groupId: string;
  entityId: string;
  channelUrl: string;
  entities: DashboardEntity[];
};

export const DetectedFilings = ({
  groupId,
  entityId,
  channelUrl,
  entities,
}: DetectedFilingsProps) => {
  const {
    data: detectedFilings,
    refetch: refetchDetectedFilings,
    ...detectedFilingsApiState
  } = useGetDetectedFilingsQuery(
    { groupId, entityId },
    {
      skip: !groupId || !entityId,
    }
  );

  const { data: filingsCount, refetch: refetchFilingsCount } =
    useGetDetectedFilingsCountQuery({ groupId }, { skip: !groupId });

  const selectedFilingsCount =
    filingsCount?.entities.find((entity) => entity.uuid === entityId)
      ?.detected_filings_count ?? 0;

  const seasonFilings = groupFilingsBySeason(detectedFilings);
  const isMultipleEntities = entities.length > 1;

  const handleNewMessage = useCallback(() => {
    if (!channelUrl) return;

    void refetchDetectedFilings();
    void refetchFilingsCount();
  }, [channelUrl, refetchDetectedFilings, refetchFilingsCount]);

  useChannelNewMessageListener(channelUrl, handleNewMessage);

  return (
    <div className="t-flex t-h-full t-flex-col t-overflow-hidden">
      <div className="t-flex t-shrink-0 t-items-center t-justify-between t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-py-3 t-px-5">
        <div className="t-flex t-shrink-0 t-items-start t-gap-2">
          <span className="t-flex t-rounded t-bg-surface-purple t-p-1 t-text-purple">
            <ListIcon />
          </span>
          <span className="t-text-subtext t-text-text-60">
            Filings detected ({selectedFilingsCount})
          </span>
        </div>
        {isMultipleEntities && (
          <div className="t-w-48">
            <EntitySelector showOnlyUSEntities size="small" />
          </div>
        )}
      </div>

      <div className="t-flex t-min-h-0 t-flex-1 t-flex-col t-overflow-hidden">
        <Async.Root
          isEmpty={seasonFilings.length === 0}
          isLoading={detectedFilingsApiState.isLoading}
          isSuccess={detectedFilingsApiState.isSuccess}
          renderOnLoad={<FilingsSkeleton />}
        >
          <Async.Empty>
            <NoFilingEmptyState />
          </Async.Empty>
          <Async.Success>
            {entityId && (
              <FilingsGroupBySeason
                entityId={entityId}
                seasonFilings={seasonFilings}
              />
            )}
          </Async.Success>
        </Async.Root>
      </div>
    </div>
  );
};
